import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Button from "./Button";
import Icon from "../AppIcon";
import LanguageSwitcher from "../LanguageSwitcher";
import LogoSolarValley from "../img/SolarValley.svg";
import { useConfig } from "../../context/ConfigContext";
import { logEvent, TRACKING_EVENTS } from "lib/analytics";

const Header = () => {
  const { t } = useTranslation();
  const config = useConfig();
  const { COMPANY_NAME, CALL_PHONE_NUMBER } = config;

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleNavClick = (name) => {
    logEvent("Navegación", "Click_menu", `Header: ${name}`);
    closeMenu();
  };

  const handleContactClick = () => {
    logEvent("Contacto", "Click_telefono", "Header: Botón Llamar");
  };

  const navigationItems = [
    { name: t("nav.home"), path: "/home", icon: "Home", originalName: "Home" },
    { name: t("nav.about"), path: "/nosotros", icon: "Info", originalName: "About" },
    { name: t("nav.projects"), path: "/proyectos", icon: "Building2", originalName: "Projects" },
    //{ name: t('nav.customer_portal'), path: '/customer-portal', icon: 'User' },
  ];

  const moreItems = [
    { name: t("nav.services"), path: "/servicios", icon: "Settings", originalName: "Services" },
    { name: t("nav.warranties"), path: "/garantias", icon: "ShieldCheck", originalName: "Warranties" },
  ];

  const isActivePath = (path) => {
    return location.pathname === path;
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled
        ? "bg-white/95 backdrop-blur-sm solar-shadow-card"
        : "bg-white"
        }`}
    >
      <div className="w-full">
        <div className="flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8">
          {/* Logo */}
          <Link
            to="/home"
            className="flex items-center space-x-3 solar-transition hover:opacity-80"
            onClick={() => handleNavClick("Logo")}
          >
            <div className="relative">
              <img src={LogoSolarValley} alt="Logo" width="100" />
            </div>
            <div className="hidden sm:block">
              <h1 className="text-xl font-bold solar-text-gradient">
                {COMPANY_NAME}
              </h1>
              <p className="text-xs text-text-secondary -mt-1">
                {t("common.clean_energy_slogan")}
              </p>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            {navigationItems?.map((item) => (
              <Link
                key={item?.path}
                to={item?.path}
                onClick={() => handleNavClick(item.originalName)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium solar-transition ${isActivePath(item?.path)
                  ? "bg-primary text-primary-foreground"
                  : "text-text-primary hover:bg-muted hover:text-foreground"
                  }`}
              >
                <Icon name={item?.icon} size={24} />
                <span>{item?.name}</span>
              </Link>
            ))}

            {/* More Dropdown */}
            <div className="relative group">
              <button className="flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium text-text-primary hover:bg-muted hover:text-foreground solar-transition">
                <Icon name="MoreHorizontal" size={24} />
                <span>{t("nav.more")}</span>
                <Icon name="ChevronDown" size={18} />
              </button>

              <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-lg solar-shadow-elevated border opacity-0 invisible group-hover:opacity-100 group-hover:visible solar-transition">
                <div className="py-2">
                  {moreItems?.map((item) => (
                    <Link
                      key={item?.path}
                      to={item?.path}
                      onClick={() => handleNavClick(item.originalName)}
                      className={`flex items-center space-x-3 px-4 py-2 text-sm solar-transition ${isActivePath(item?.path)
                        ? "bg-primary text-primary-foreground"
                        : "text-text-primary hover:bg-muted"
                        }`}
                    >
                      <Icon name={item?.icon} size={24} />
                      <span>{item?.name}</span>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </nav>

          {/* CTA Button */}
          <div className="hidden md:flex items-center space-x-4">
            {/*<LanguageSwitcher />*/}
            <Button
              variant="outline"
              size="sm"
              iconName="Phone"
              iconPosition="left"
              className="text-sm"
              href={`tel:${CALL_PHONE_NUMBER}`}
              onClick={handleContactClick}
            >
              {t("common.contact")}
            </Button>
            <Link to="/calculadora" onClick={() => handleNavClick("Calculate Savings CTA")}>
              <Button
                variant="default"
                size="sm"
                iconName="Calculator"
                iconPosition="left"
                className="bg-cta-orange hover:bg-cta-orange/90 text-sm"
              >
                {t("common.calculate_savings")}
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <div className="lg:hidden flex items-center space-x-2">
            {/*<LanguageSwitcher />*/}
            <button
              onClick={toggleMenu}
              className="p-2 rounded-lg text-text-primary hover:bg-muted solar-transition"
              aria-label="Toggle menu"
            >
              <Icon name={isMenuOpen ? "X" : "Menu"} size={24} />
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <div
          className={`lg:hidden bg-white border-t solar-transition-slow overflow-hidden ${isMenuOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
            }`}
        >
          <nav className="px-4 py-4 space-y-2">
            {[...navigationItems, ...moreItems]?.map((item) => (
              <Link
                key={item?.path}
                to={item?.path}
                onClick={closeMenu}
                className={`flex items-center space-x-3 px-4 py-3 rounded-lg solar-transition ${isActivePath(item?.path)
                  ? "bg-primary text-primary-foreground"
                  : "text-text-primary hover:bg-muted"
                  }`}
              >
                <Icon name={item?.icon} size={20} />
                <span className="font-medium">{item?.name}</span>
              </Link>
            ))}

            <div className="pt-4 border-t space-y-2">
              <Button
                variant="outline"
                fullWidth
                iconName="Phone"
                iconPosition="left"
                href={`tel:${CALL_PHONE_NUMBER}`}
                onClick={closeMenu}
              >
                {t("common.contact")}
              </Button>
              <Link to="/calculadora">
                <Button
                  variant="default"
                  fullWidth
                  iconName="Calculator"
                  iconPosition="left"
                  className="bg-cta-orange hover:bg-cta-orange/90"
                  onClick={closeMenu}
                >
                  {t("common.calculate_savings")}
                </Button>
              </Link>
            </div>
          </nav>
        </div>
      </div>
    </header>
  );
};

export default Header;
